import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Heading,
  Text,
  Badge,
  Skeleton,
  Flex,
  useColorModeValue,
  Divider,
  Code,
  useColorMode,
  Icon,
  Button,
  IconButton,
  HStack,
  VStack,
  Input,
  Wrap,
  WrapItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react'
import { FaSync } from 'react-icons/fa'

import { formatDate, parseDowntime } from '../utils/helpers'
import { dataService } from '../services/api'
import { useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import EntryDetailModal from './EntryDetailModal'
import FeaturesDisplay from './FeaturesDisplay'
import type {
  DataEntry,
  EntryRecord,
  EntriesV2Response,
  FileRecord,
  User,
  DataFeature,
  TargetSymbol,
} from '../types'

interface DataEntryTableProps {
  recordId: number
  data: {
    record: EntryRecord
    files: FileRecord[]
    user: User
    features_original?: DataFeature[]
    features_sorted?: DataFeature[]
    target_symbols?: TargetSymbol[]
  } | null
  isLoading: boolean
  error: unknown
}

const DataEntryTable: React.FC<DataEntryTableProps> = ({
  recordId,
  data,
  isLoading,
  error,
}) => {
  const { colorMode } = useColorMode()
  const tableBg = useColorModeValue('white', 'gray.800')
  const codeBg = useColorModeValue('gray.50', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const headerTextColor = useColorModeValue('gray.600', 'gray.300')
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50')

  const normalizeEpochMs = (ts: number | null | undefined) => {
    if (ts === null || ts === undefined) return null
    const n = Number(ts)
    if (Number.isNaN(n)) return null
    // Heuristic: < 1e12 is very likely seconds; >= 1e12 is ms.
    return n < 1e12 ? n * 1000 : n
  }

  // Format time with difference from base timestamp - returns object for multi-line display
  const formatTimeWithDiff = (
    timestamp: number | null | undefined,
    baseTimestamp: number | null | undefined
  ): { date: string; time: string; diff: string } | null => {
    const normalizedTs = normalizeEpochMs(timestamp)
    const normalizedBase = normalizeEpochMs(baseTimestamp)
    
    if (!normalizedTs) return null
    
    const formattedTime = formatDate(normalizedTs)
    
    // formatDate returns: "2026-02-05 14:48:46.604777+08:00"
    const parts = formattedTime.split(' ')
    const datePart = parts[0] || formattedTime
    const timePart = parts.slice(1).join(' ') || ''
    
    let diffStr = ''
    if (normalizedBase) {
      const diff = (normalizedTs - normalizedBase) / 1000
      diffStr = `(${diff > 0 ? '+' : ''}${diff}ms)`
    }
    
    return {
      date: datePart,
      time: timePart,
      diff: diffStr
    }
  }

  const formatNumber = (num: number) => {
    return Number(num.toFixed(6)).toString()
  }

  const formatDataValues = (dataObj: Record<string, any>) => {
    if (!dataObj || typeof dataObj !== 'object') return dataObj

    return Object.entries(dataObj).reduce((acc, [key, value]) => {
      // If value is a number, format it
      if (typeof value === 'number') {
        acc[key] = formatNumber(value)
      } else {
        // Keep other values as is
        acc[key] = value
      }
      return acc
    }, {} as Record<string, any>)
  }

  const record = data?.record || null
  const user = data?.user || null

  const symbols = record?.symbols ?? []
  const targetSymbols = data?.target_symbols ?? []
  const tabSymbols = symbols
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const activeSymbol = tabSymbols[activeTabIndex]

  // If start time is selected, query backend from that timestamp; otherwise fetch latest data.
  // Using datetime-local keeps the UX simple and avoids extra date picker deps.
  const [startTimeLocal, setStartTimeLocal] = useState<string>('')
  const startTimestampSec = useMemo(() => {
    if (!startTimeLocal) return undefined
    const ms = new Date(startTimeLocal).getTime()
    if (Number.isNaN(ms)) return undefined
    return Math.floor(ms / 1000)
  }, [startTimeLocal])
  const latestData = startTimestampSec === undefined

  // Pagination removed: fetch a single (large) page for the active symbol.
  const pageSize = 1000
  const entriesQuery = useQuery({
    queryKey: [
      'entries',
      recordId,
      activeSymbol,
      pageSize,
      latestData,
      startTimestampSec,
      record?.type,
    ],
    queryFn: async () => {
      if (!activeSymbol) {
        // should not happen when enabled is true
        return Promise.resolve(null as unknown as EntriesV2Response)
      }
   
      const resp = await dataService.getDataEntries(recordId, {
        symbol: [activeSymbol],
        size: pageSize,
        latest_data: latestData,
        start_timestamp: latestData ? undefined : startTimestampSec,
      })
      return resp
    },
    enabled: !!recordId && !!activeSymbol,
  })

  const entries: DataEntry[] = useMemo(() => {
    if (!activeSymbol) return []
    if (!entriesQuery.data?.data?.entries) return []
    const direct = entriesQuery.data.data.entries?.[activeSymbol]
    if (direct) return direct
    const firstKey = Object.keys(entriesQuery.data.data.entries ?? {})[0]
    return firstKey ? entriesQuery.data.data.entries[firstKey] ?? [] : []
  }, [entriesQuery.data, activeSymbol])

  const entriesScrollRef = useRef<HTMLDivElement | null>(null)
  // Which symbol the current scroll container belongs to (avoids cross-tab timing issues)
  const scrollContainerSymbolRef = useRef<string | undefined>(undefined)
  const scrollPositionsRef = useRef<Record<string, number>>({})
  const rowHeight = 90
  const virtualCount = activeSymbol ? entries.length : 0

  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    getScrollElement: () => entriesScrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedEntry, setSelectedEntry] = useState<DataEntry | null>(null)

  const openEntryDetail = (entry: DataEntry) => {
    setSelectedEntry(entry)
    onOpen()
  }

  // Features modal state
  const { isOpen: isFeaturesOpen, onOpen: onFeaturesOpen, onClose: onFeaturesClose } = useDisclosure()
  const [selectedFeatures, setSelectedFeatures] = useState<{ title: string; data: DataFeature[] } | null>(null)

  const openFeaturesModal = (title: string, features: DataFeature[]) => {
    setSelectedFeatures({ title, data: features })
    onFeaturesOpen()
  }

  useLayoutEffect(() => {
    if (!activeSymbol) return

    const savedTop = scrollPositionsRef.current[activeSymbol] ?? 0
    let cancelled = false
    let attempts = 0

    const apply = () => {
      if (cancelled) return
      const node = entriesScrollRef.current
      const nodeSymbol = scrollContainerSymbolRef.current
      if (node && nodeSymbol === activeSymbol) {
        node.scrollTop = savedTop
        rowVirtualizer.measure()
        return
      }
      // In lazy/unmount tab switching, the scroll node can be null for a short moment.
      if (attempts < 10) {
        attempts += 1
        requestAnimationFrame(apply)
      }
    }

    requestAnimationFrame(apply)
    return () => {
      cancelled = true
    }
  }, [activeSymbol, rowVirtualizer])

  const handleEntriesScroll = () => {
    const el = entriesScrollRef.current
    if (!el) return
    const symbolForNode = scrollContainerSymbolRef.current
    if (symbolForNode) scrollPositionsRef.current[symbolForNode] = el.scrollTop
  }

  const handleTabChange = useCallback(
    (nextIndex: number) => {
      const el = entriesScrollRef.current
      const symbolForNode = scrollContainerSymbolRef.current
      if (el && symbolForNode) scrollPositionsRef.current[symbolForNode] = el.scrollTop
      setActiveTabIndex(nextIndex)
    },
    []
  )

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </Text>
      </Box>
    )
  }

  return (
    <Box>
      {/* Record Information */}
      <Box mb={6}>
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <Heading size="lg" mb={2}>
            {record?.type} Record Detail
          </Heading>
        </Flex>

        {/* Record Data Table */}
        <TableContainer
          borderRadius="md"
          boxShadow="sm"
          bg={tableBg}
          overflowX="auto"
          border="1px"
          borderColor={borderColor}
        >
          <Table variant="simple" size="md">
            <Tbody>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  ID
                </Td>
                <Td>{record?.record_id || <Skeleton height="20px" width="50px" />}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Record Type</Td>
                <Td>
                  {record ? (
                    <Badge
                      colorScheme={record.type === 'Data' ? 'teal' : 'purple'}
                      fontSize="md"
                      p={1}
                    >
                      {record.type}
                    </Badge>
                  ) : (
                    <Skeleton height="24px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  Name
                </Td>
                <Td>
                  {record?.name || <Skeleton height="20px" width="50px" />}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  Provider Name
                </Td>
                <Td>
                  {user ? (
                    <Text>{user.name || user.id} </Text>
                  ) : (
                    <Skeleton height="20px" width="50px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" width="150px">
                  Provider Contact
                </Td>
                <Td>
                  {user ? (
                    <Text>{user.contact || 'Not Provided'} </Text>
                  ) : (
                    <Skeleton height="20px" width="50px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" verticalAlign="top">
                  Target Symbols
                </Td>
                <Td>
                  {record ? (
                    targetSymbols.length ? (
                      <Wrap spacing={2}>
                        {targetSymbols.map((targetSymbol) => (
                          <WrapItem key={`${targetSymbol.symbol_id}-${targetSymbol.symbol}`}>
                            <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>
                              {targetSymbol.symbol}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )
                  ) : (
                    <Skeleton height="24px" width="120px" />
                  )}
                </Td>
              </Tr>
   

              <Tr>
                <Td fontWeight="bold">Encryption</Td>
                <Td>
                  {record ? (
                    <Badge
                      colorScheme={record.is_encrypted ? 'yellow' : 'gray'}
                      color={record.is_encrypted ? 'yellow.800' : 'gray.700'}
                      fontSize="xs"
                      p={1}
                    >
                      {record.is_encrypted ? 'ENCRYPTED' : 'Unencrypted'}
                    </Badge>
                  ) : (
                    <Skeleton height="24px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Update Frequency</Td>
                <Td>
                  {record ? (
                    record?.update_frequency + ' ms'
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>

              <Tr>
                <Td fontWeight="bold">Created At</Td>
                <Td>
                  {record ? (
                    formatDate(record.created_at)
                  ) : (
                    <Skeleton height="20px" width="150px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Last Updated</Td>
                <Td>
                  {record ? (
                    formatDate(record.last_updated)
                  ) : (
                    <Skeleton height="20px" width="150px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Live Start Time</Td>
                <Td>
                  {record ? (
                    record.live_start_time ? (
                      formatDate(record.live_start_time)
                    ) : (
                      'null'
                    )
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Downtime%</Td>
                <Td>
                  {record ? (
                    <HStack spacing={4} flexWrap="wrap">
                      {[
                        { label: '1h', period: '1h' },
                        { label: '6h', period: '6h' },
                        { label: '12h', period: '12h' },
                        { label: '1d', period: '1d' },
                        { label: '7d', period: '7d' },
                        { label: '30d', period: '30d' },
                      ].map(({ label, period }) => (
                        <Text key={period} fontSize="sm">
                          <Text as="span" color="gray.500">{label}:</Text>{' '}
                          <Text as="span" fontWeight="medium">
                            {/* todo: 暂时写死 */}
                            {/* { isActive ? parseDowntime(record.downtime, period) : '-' } */}
                            {parseDowntime(record.downtime, period)}
                          </Text>
                        </Text>
                      ))}
                    </HStack>
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Fee(USD/Month)</Td>
                <Td>
                  {record ? (
                    record.fee
                  ) : (
                    <Skeleton height="20px" width="80px" />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold" verticalAlign="top">
                  Description
                </Td>
                <Td>
                  {record ? (
                    <Box
                      maxHeight="100px"
                      overflowY="auto"
                      p={2}
                      borderRadius="md"
                      bg={codeBg}
                      css={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          width: '10px',
                          background:
                            colorMode === 'light'
                              ? 'rgba(0,0,0,0.05)'
                              : 'rgba(255,255,255,0.05)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background:
                            colorMode === 'light'
                              ? 'rgba(0,0,0,0.2)'
                              : 'rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                        },
                      }}
                    >
                      <Text fontWeight="medium">{record.description}</Text>
                    </Box>
                  ) : (
                    <Skeleton height="100px" width="100%" />
                  )}
                </Td>
              </Tr>
              {record && (
                <FeaturesDisplay
                  label="Features Original"
                  features={data?.features_original}
                  codeBg={codeBg}
                  onViewAll={openFeaturesModal}
                />
              )}
              {record && (
                <FeaturesDisplay
                  label="Features Sorted"
                  features={data?.features_sorted}
                  codeBg={codeBg}
                  onViewAll={openFeaturesModal}
                />
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>


      <Divider my={6} />

      {/* Entries Table */}
      <Box>
        <Flex align="center" justify="space-between" gap={3} mb={4} flexWrap="wrap">
          <Heading size="md">Data Entries</Heading>
          <HStack spacing={2} flexWrap="wrap" justify="flex-end">
            <Input
              type="datetime-local"
              size="sm"
              value={startTimeLocal}
              onChange={(e) => setStartTimeLocal(e.target.value)}
              isDisabled={!activeSymbol}
              width="260px"
              title="Select a start time to load data from that timestamp"
            />
            {!!startTimeLocal && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStartTimeLocal('')}
                isDisabled={!activeSymbol}
              >
                Latest
              </Button>
            )}
            <IconButton
              aria-label="Refresh entries"
              icon={<Icon as={FaSync} />}
              size="sm"
              variant="ghost"
              isDisabled={!activeSymbol}
              isLoading={entriesQuery.isRefetching || entriesQuery.isFetching}
              onClick={() => entriesQuery.refetch()}
            />
          </HStack>
        </Flex>

        {tabSymbols.length > 0 ? (
          <Tabs
            variant="enclosed"
            colorScheme="blue"
            isLazy
            lazyBehavior="unmount"
            index={activeTabIndex}
            onChange={handleTabChange}
          >
            <TabList
              mb="1em"
              overflowX="auto"
              overflowY="hidden"
            >
              {tabSymbols.map((symbol) => (
                <Tab key={symbol} whiteSpace="nowrap">
                  {symbol}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {tabSymbols.map((symbol) => {
                const isCurrent = symbol === activeSymbol
                return (
                  <TabPanel key={symbol} px={0}>
                    <Box
                      borderRadius="md"
                      boxShadow="sm"
                      bg={tableBg}
                      border="1px"
                      borderColor={borderColor}
                      overflow="hidden"
                    >
                      {/* header */}
                      <Flex
                        px={4}
                        py={3}
                        borderBottom="1px"
                        borderColor={borderColor}
                        fontSize="sm"
                        fontWeight="semibold"
                        color={headerTextColor}
                        overflowY="scroll"
                        css={{
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: 'transparent',
                          },
                        }}
                      >
                        <Box flex="0 0 175px">ID</Box>
                        <Box flex="0 0 110px">Batch ID</Box>
                        <Box flex="0 0 210px">As Of Time</Box>
                        <Box flex="0 0 210px">Time</Box>
                        <Box flex={{ base: '0 0 150px', '2xl': '0 0 270px' }}>Runner Time</Box>
                        <Box flex={{ base: '0 0 150px', '2xl': '0 0 270px' }}>Server Time</Box>
                        <Box flex="0 0 140px">Target Symbol</Box>
                        <Box flex="1 1 auto">{record?.is_encrypted ? 'Encrypted Payload' : 'Data'}</Box>
                      </Flex>

                      {isLoading || (isCurrent && entriesQuery.isLoading) ? (
                        <Box p={4}>
                          {Array.from({ length: 13 }).map((_, i) => (
                            <Skeleton key={i} height={`${rowHeight}px`} mb={2} />
                          ))}
                        </Box>
                      ) : isCurrent ? (
                        <Box
                          ref={(node) => {
                            entriesScrollRef.current = node
                            scrollContainerSymbolRef.current = node ? symbol : undefined
                          }}
                          maxH="60vh"
                          overflowY="auto"
                          onScroll={handleEntriesScroll}
                        >
                          {entries.length === 0 ? (
                            <Box p={8} textAlign="center">
                              <Text color="gray.500">No data entries found for {symbol}</Text>
                            </Box>
                          ) : (
                            <Box
                              height={`${rowVirtualizer.getTotalSize()}px`}
                              position="relative"
                            >
                              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const idx = virtualRow.index
                                const entry = entries[idx]

                                return (
                                  <Flex
                                    key={virtualRow.key}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    transform={`translateY(${virtualRow.start}px)`}
                                    minHeight={`${virtualRow.size}px`}
                                    px={4}
                                    py={2}
                                    align="center"
                                    borderBottom="1px"
                                    borderColor={borderColor}
                                    _hover={{
                                      bg: rowHoverBg,
                                    }}
                                  >
                                    <>
                                      <Box flex="0 0 175px">{entry?.id}</Box>
                                      <Box flex="0 0 110px">
                                        {entry?.batch_id !== undefined && entry?.batch_id !== null
                                          ? entry.batch_id
                                          : '-'}
                                      </Box>
                                      <Box flex="0 0 210px">
                                        {entry && entry.as_of_timestamp !== null && entry.as_of_timestamp !== undefined
                                          ? formatDate(normalizeEpochMs(entry.as_of_timestamp))
                                          : '-'}
                                      </Box>
                                      <Box flex="0 0 210px">
                                        {entry
                                          ? formatDate(normalizeEpochMs(entry.timestamp))
                                          : '-'}
                                      </Box>
                                      <Box flex={{ base: '0 0 150px', '2xl': '0 0 270px' }}>
                                        {entry ? (
                                          (() => {
                                            const timeData = formatTimeWithDiff(entry.runner_timestamp, entry.timestamp)
                                            return timeData ? (
                                              <Box>
                                                <Text display={{ base: 'none', '2xl': 'block' }} fontSize="sm">
                                                  {timeData.date} {timeData.time} {timeData.diff}
                                                </Text>
                                                <VStack 
                                                  display={{ base: 'flex', '2xl': 'none' }} 
                                                  align="flex-start" 
                                                  spacing={0} 
                                                  fontSize="xs"
                                                >
                                                  <Text>{timeData.date}</Text>
                                                  <Text>{timeData.time}</Text>
                                                  <Text color="gray.500">{timeData.diff}</Text>
                                                </VStack>
                                              </Box>
                                            ) : '-'
                                          })()
                                        ) : '-'}
                                      </Box>
                                      <Box flex={{ base: '0 0 150px', '2xl': '0 0 270px' }}>
                                        {entry ? (
                                          (() => {
                                            const timeData = formatTimeWithDiff(entry.server_timestamp, entry.timestamp)
                                            return timeData ? (
                                              <Box>
                                                <Text display={{ base: 'none', '2xl': 'block' }} fontSize="sm">
                                                  {timeData.date} {timeData.time} {timeData.diff}
                                                </Text>
                                                <VStack 
                                                  display={{ base: 'flex', '2xl': 'none' }} 
                                                  align="flex-start" 
                                                  spacing={0} 
                                                  fontSize="xs"
                                                >
                                                  <Text>{timeData.date}</Text>
                                                  <Text>{timeData.time}</Text>
                                                  <Text color="gray.500">{timeData.diff}</Text>
                                                </VStack>
                                              </Box>
                                            ) : '-'
                                          })()
                                        ) : '-'}
                                      </Box>
                                      <Box flex="0 0 140px">{symbol}</Box>
                                      <Flex flex="1 1 auto" minW={0} align="center" gap={3}>
                                        <Code
                                          fontSize="xs"
                                          bg={codeBg}
                                          px={2}
                                          borderRadius="md"
                                          noOfLines={1}
                                          maxW="100%"
                                        >
                                          {record?.is_encrypted
                                            ? entry?.encrypted_payload || 'No encrypted payload'
                                            : entry
                                              ? JSON.stringify(formatDataValues(entry.data), null, 2)
                                              : '-'}
                                        </Code>
                                        {entry && (
                                          <Button
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => openEntryDetail(entry)}
                                          >
                                            View
                                          </Button>
                                        )}
                                      </Flex>
                                    </>
                                  </Flex>
                                )
                              })}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box p={8} textAlign="center">
                          <Text color="gray.500">Switch to this tab to load entries</Text>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>
                )
              })}
            </TabPanels>
          </Tabs>
        ) : (
           <TableContainer
             borderRadius="md"
             boxShadow="sm"
             bg={tableBg}
             overflowX="auto"
             border="1px"
             borderColor={borderColor}
           >
             <Table variant="simple" size="md">
               <Thead>
                 <Tr>
                   <Th>ID</Th>
                   <Th>Batch ID</Th>
                  <Th>As Of Time</Th>
                   <Th>Time</Th>
                   <Th>Runner Time</Th>
                   <Th>Server Time</Th>
                 <Th>Target Symbol</Th>
                  <Th>{record?.is_encrypted ? 'Encrypted Payload' : 'Data'}</Th>
                 </Tr>
               </Thead>
               <Tbody>
                 <Tr>
                  <Td colSpan={8} textAlign="center" py={6}>
                      <Text color="gray.500">No data entries found</Text>
                    </Td>
                 </Tr>
               </Tbody>
             </Table>
           </TableContainer>
        )}
      </Box>

      <EntryDetailModal
        isOpen={isOpen}
        onClose={onClose}
        selectedEntry={selectedEntry}
        record={record}
        codeBg={codeBg}
        formatDataValues={formatDataValues}
        maxBodyHeight="70vh"
      />

      {/* Features Detail Modal */}
      <Modal isOpen={isFeaturesOpen} onClose={onFeaturesClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" py={3}>{selectedFeatures?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4} pt={0}>
            <Box
              maxH="70vh"
              overflowY="auto"
              css={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '10px',
                  background:
                    colorMode === 'light'
                      ? 'rgba(0,0,0,0.05)'
                      : 'rgba(255,255,255,0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  background:
                    colorMode === 'light'
                      ? 'rgba(0,0,0,0.2)'
                      : 'rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              <Code
                fontSize="xs"
                bg={codeBg}
                p={3}
                borderRadius="md"
                whiteSpace="pre"
                display="block"
                w="100%"
                lineHeight="1.4"
              >
                {selectedFeatures?.data
                  ? JSON.stringify(selectedFeatures.data, null, 2)
                  : ''}
              </Code>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  )
}

export default DataEntryTable
